"use client";

import { FormEvent, useRef, useState, useTransition } from "react";

type ContactResponse = {
  Success?: boolean;
  Message?: string;
};

export function ContactSection() {
  const formRef = useRef<HTMLFormElement>(null);
  const fullNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const numberRef = useRef<HTMLInputElement>(null);
  const purposeRef = useRef<HTMLSelectElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const sourceRef = useRef<HTMLSelectElement>(null);
  const [notice, setNotice] = useState<{ type: "success" | "warning" | "error"; message: string } | null>(null);
  const [isPending, startTransition] = useTransition();

  function warn(message: string, element?: HTMLElement | null) {
    setNotice({ type: "warning", message });
    element?.focus();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const form = event.currentTarget;
    const formData = new FormData(form);
    const fullname = String(formData.get("fullName") ?? "").trim();
    const companyName = String(formData.get("companyName") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const contactNumber = String(formData.get("contactNumber") ?? "").trim();
    const purpose = String(formData.get("purpose") ?? "0");
    const source = String(formData.get("source") ?? "None");
    const message = String(formData.get("message") ?? "").trim();

    if (fullname === "") {
      warn("Please enter your name", fullNameRef.current);
      return;
    }

    if (email === "") {
      warn("Please enter your email address", emailRef.current);
      return;
    }

    if (contactNumber === "") {
      warn("Please enter your contact number", numberRef.current);
      return;
    }

    if (purpose === "0") {
      warn("Please select the purpose for contacting us", purposeRef.current);
      return;
    }

    if (message === "") {
      warn("Please enter a short message", messageRef.current);
      return;
    }

    if (source === "None") {
      warn("Please tel us how you hear about Newsclip", sourceRef.current);
      return;
    }

    setNotice(null);

    startTransition(async () => {
      const response = await fetch("/api/contactus", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          Fullname: fullname,
          CompanyName: companyName,
          EmailAddress: email,
          ContactNumber: contactNumber,
          Purpose: purpose,
          Source: source,
          Message: message
        })
      });

      if (!response.ok) {
        setNotice({ type: "error", message: "Unable to connect to the Newsclip service" });
        return;
      }

      const data = (await response.json()) as ContactResponse;

      if (data.Success === false) {
        setNotice({ type: "warning", message: data.Message ?? "Unable to send your message" });
        return;
      }

      setNotice({ type: "success", message: data.Message ?? "Your message has been sent successfully" });
      formRef.current?.reset();
    });
  }

  return (
    <section className="section section-inverse cta" id="contact">
      <div className="container cta-grid">
        <div>
          <h2>{"Let's work together"}</h2>
          <p>
            Ready to transform your media strategy with data-driven insights? Get in touch with our team of
            specialists today.
          </p>
          <div className="contact-list">
            <a className="contact-link" href="tel:+27100205250">
              <span className="contact-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" role="img">
                  <path d="M6.6 10.8c1.6 3.1 3.5 5 6.6 6.6l2.2-2.2c.3-.3.8-.4 1.2-.3 1.3.4 2.6.6 4 .6.7 0 1.2.5 1.2 1.2v3.5c0 .7-.5 1.2-1.2 1.2C10.7 21.4 2.6 13.3 2.6 3.4c0-.7.5-1.2 1.2-1.2h3.5c.7 0 1.2.5 1.2 1.2 0 1.4.2 2.7.6 4 .1.4 0 .9-.3 1.2l-2.2 2.2Z" />
                </svg>
              </span>
              +27 10 020 5250
            </a>
            <a className="contact-link" href="mailto:info@newsclip.co.za">
              <span className="contact-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" role="img">
                  <path d="M4 5h16c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V7c0-1.1.9-2 2-2Zm8 7.4L4.2 7.2V17h15.6V7.2L12 12.4Zm0-2.2L18.7 7H5.3L12 10.2Z" />
                </svg>
              </span>
              info@newsclip.co.za
            </a>
          </div>
        </div>
        <form className="contact-form" ref={formRef} onSubmit={handleSubmit}>
          {notice ? (
            <div className={`form-notice ${notice.type}`} role="status">
              {notice.message}
            </div>
          ) : null}
          <div className="form-row">
            <div className="field">
              <label htmlFor="full-name">Full name</label>
              <input id="full-name" name="fullName" type="text" autoComplete="name" ref={fullNameRef} />
            </div>
            <div className="field">
              <label htmlFor="company-name">Company name</label>
              <input id="company-name" name="companyName" type="text" autoComplete="organization" />
            </div>
          </div>
          <div className="form-row">
            <div className="field">
              <label htmlFor="email">Email</label>
              <input id="email" name="email" type="email" autoComplete="email" ref={emailRef} />
            </div>
            <div className="field">
              <label htmlFor="contact-number">Contact number</label>
              <input id="contact-number" name="contactNumber" type="tel" autoComplete="tel" ref={numberRef} />
            </div>
          </div>
          <div className="form-row">
            <div className="field">
              <label htmlFor="purpose">Purpose</label>
              <select id="purpose" name="purpose" defaultValue="0" ref={purposeRef}>
                <option value="0" disabled>
                  Purpose*
                </option>
                <option value="1">Sales Enquiry</option>
                <option value="2">Request for Quotation</option>
                <option value="3">Career-related Enquiry</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="source">How did you hear about Newsclip?</label>
              <select id="source" name="source" defaultValue="None" ref={sourceRef}>
                <option value="None" disabled>
                  How did you hear about Newsclip?*
                </option>
                <option value="From a friend">From a friend</option>
                <option value="From a client">From a client</option>
                <option value="Brandfootprints">Brandfootprints</option>
                <option value="Google search">Google search</option>
                <option value="Social media">Social media</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          <div className="field">
            <label htmlFor="message">Custom message</label>
            <textarea id="message" name="message" rows={4} ref={messageRef} />
          </div>
          <button className="button" type="submit" disabled={isPending}>
            {isPending ? "Sending..." : "Get In Touch"}
          </button>
        </form>
      </div>
    </section>
  );
}
